import {PeriodicityEnum} from "../enum/enum";
import {arrayLast, getRandomNumber, getRandomStr, isNumber, isString, nnTrim} from "../util";
import {Base} from "../Base";

const YEARLESS_YEAR = 2001;

/**
 * A calculator for dates
 * @extends Base
 */
class TimeCalculator extends Base {

    /**
     *
     * @param periodicity {PeriodicityEnum} The periodicity of the layer
     * @param from {string|Date} Min date in format YYYY-MM-DD
     * @param until {string|Date} Max date in format YYYY-MM-DD
     * @param translator {Translator} The translator
     */
    constructor(periodicity, from = undefined, until = undefined, translator = undefined) {
        super();
        this.periodicity = periodicity ? periodicity : PeriodicityEnum.CUSTOM;
        this.from = from ? this.fixDate(dateFromStr(from)) : undefined;
        this.until = until ? this.fixUnboundedDateUntil(dateFromStr(until)) : undefined;
        if (this.isYearless()) {
            this.from = this.from ? dateFromYearMonthDay(YEARLESS_YEAR, this.getMonthFromDate(this.from), this.getDayFromDate(this.from)) : undefined;
            this.until = this.until ? dateFromYearMonthDay(YEARLESS_YEAR, this.getMonthFromDate(this.until), this.getDayFromDate(this.until)) : undefined;
        }
        this.translator = translator;
    }

    getClassName() {
        return "TimeCalculator";
    }

    /**
     * Ther periodicity
     * @return {PeriodicityEnum}
     */
    getPeriodicity() {
        return this.periodicity;
    }

    /**
     * The min date
     * @return {Date}
     */
    getFrom() {
        return this.from;
    }

    /**
     * The max date
     * @return {Date}
     */
    getUntil() {
        return this.until;
    }

    /**
     * Is this a yearly circular periodicity?
     * @return {boolean} true if it is
     */
    isYearless() {
        return this.isTenDailyYearless() || this.isDailyYearless() || this.isTenDailyYearless30Day();
    }

    isTimeless() {
        return this.isLatest();
    }

    hasNoDate() {
        return !this.periodicity || this.isUnknown();
    }

    /**
     *
     * @return {boolean}
     */
    isLatest() {
        return this.periodicity === PeriodicityEnum.LATEST;
    }

    /**
     *
     * @return {boolean}
     */
    isYearly() {
        return this.periodicity === PeriodicityEnum.YEARLY;
    }

    /**
     *
     * @return {boolean}
     */
    isMonthly() {
        return this.periodicity === PeriodicityEnum.MONTHLY;
    }

    /**
     *
     * @return {boolean}
     */
    isMonthlyForecast() {
        return this.periodicity === PeriodicityEnum.MONTHLY_FORECAST;
    }

    /**
     *
     * @return {boolean}
     */
    isMonthlyUpdatedTenDaily() {
        return this.periodicity === PeriodicityEnum.MONTHLY_UPDATED_TEN_DAILY;
    }

    /**
     *
     * @return {boolean}
     */
    isTenDaily() {
        return this.periodicity === PeriodicityEnum.TEN_DAILY;
    }

    /**
     *
     * @return {boolean}
     */
    isTenDailyYearless() {
        return this.periodicity === PeriodicityEnum.TEN_DAILY_YEARLESS;
    }

    /**
     *
     * @return {boolean}
     */
    isTenDailyYearless30Day() {
        return this.periodicity === PeriodicityEnum.TEN_DAILY_YEARLESS_30DAY;
    }

    /**
     *
     * @return {boolean}
     */
    isDailyYearless() {
        return this.periodicity === PeriodicityEnum.DAILY_YEARLESS;
    }

    /**
     *
     * @return {boolean}
     */
    isWeekly() {
        return this.periodicity === PeriodicityEnum.WEEKLY;
    }

    /**
     *
     * @return {boolean}
     */
    isISOWeekly() {
        return this.periodicity === PeriodicityEnum.ISO_WEEKLY;
    }

    /**
     *
     * @return {boolean}
     */
    isDaily() {
        return this.periodicity === PeriodicityEnum.DAILY;
    }

    /**
     *
     * @return {boolean}
     */
    isUnknown() {
        return this.periodicity === PeriodicityEnum.CUSTOM;
    }

    /**
     * Get all the years of available data
     * @return {Array<Number>} (integer)
     */
    getYears() {
        const years = [];
        const untilYear = this.getYearFromDate(this.until);
        for (let y = this.getYearFromDate(this.from); y <= untilYear; y++)
            years.push(y);
        return years;
    }

    /**
     * Is the given time code valid?
     * @param timeCode {string} YYYY-MM-DD
     * @return {boolean}
     */
    isValid(timeCode) {
        const year = this.parseYear(timeCode);
        if (!verifyDateString(timeCode) || (!this.isYearless() && year < 1900) || year > 3000) {
            return false;
        }

        if (this.from && dateFromStr(timeCode).getTime() < this.from.getTime())
            return false;

        if (this.until && dateFromStr(timeCode).getTime() > this.until.getTime())
            return false;

        if (this.isYearly()) {
            try {
                const month = this.parseMonth(timeCode);
                const day = this.parseDay(timeCode);
                return month === 1 && day === 1;
            } catch (e) {
                return false;
            }
        } else if (this.isMonthly()) {
            try {
                return this.parseDay(timeCode) === 1;
            } catch (e) {
                return false;
            }
        } else if (this.isMonthlyForecast()) {
            try {
                return this.parseDay(timeCode) === 1;
            } catch (e) {
                return false;
            }
        } else if (this.isTenDaily() || this.isMonthlyUpdatedTenDaily() || this.isTenDailyYearless() || this.isTenDailyYearless30Day()) {
            try {
                const tenDays = this.parseTenDays(timeCode);
                return tenDays >= 1 && tenDays <= 3;
            } catch (e) {
                return false;
            }
        } else if (this.isWeekly()) {
            try {
                const year = this.parseYear(timeCode);
                const week = this.parseWeek(timeCode);
                const date = dateFromStr(this.writeWeekly(year, week));
                const firstDayOfFirstWeek = this.getFirstDayOfFirstWeek(year);
                return this.getDifferenceInDays(date, firstDayOfFirstWeek) % 7 === 0;
            } catch (e) {
                return false;
            }
        } else if (this.isISOWeekly()) {
            try {
                const year = this.parseYear(timeCode);
                const week = this.parseISOWeek(timeCode);
                const date = dateFromStr(this.writeISOWeekly(year, week));
                const firstDayOfFirstWeek = this.getFirstDayOfFirstISOWeek(year);
                return this.getDifferenceInDays(date, firstDayOfFirstWeek) % 7 === 0;
            } catch (e) {
                return false;
            }
        }
        return true;
    }

    /**
     * Parse the year of the given time code
     * @param timeCode {string}
     * @return {undefined|number}
     */
    parseYear(timeCode) {
        if (this.isYearless()) {
            const split = timeCode.split('-');
            return split.length === 3 ? Number(split[0]) : 0;
        }
        return this.isUnknown() ? undefined : Number(timeCode.split('-')[0]);
    }

    /**
     * Parse the month of the given time code
     * @param timeCode {string}
     * @return {undefined|number} [1 .. 12]
     */
    parseMonth(timeCode) {
        return this.isUnknown() || this.isYearly() ? undefined : Number(timeCode.split('-')[1]);
    }

    /**
     * Parse the ten day period of the given time code
     * @param timeCode {string}
     * @return {undefined|number} [1 .. 3]
     */
    parseTenDays(timeCode) {
        if (!this.isTenDaily() && !this.isMonthlyUpdatedTenDaily() && !this.isTenDailyYearless() && !this.isTenDailyYearless30Day()) {
            return undefined;
        }
        const split = timeCode.split('-');
        const day = split.length === 3 ? Number(split[2]) : Number(split[1]);
        switch (day) {
            case 1:
                return 1;
            case 11:
                return 2;
            case 21:
                return 3;
            default:
                return -1;
        }
    }

    /**
     * Parse the week of the given time code
     * @param timeCode {string}
     * @return {undefined|number} [1 .. 53]
     */
    parseWeek(timeCode) {
        if (!this.isWeekly()) {
            return undefined;
        }
        return this.getWeekFromDate(dateFromStr(timeCode));
    }

    /**
     * Parse the ISO week of the given time code
     * @param timeCode {string}
     * @return {undefined|number} [1 .. 53]
     */
    parseISOWeek(timeCode) {
        if (!this.isISOWeekly()) {
            return undefined;
        }
        return this.getISOWeekFromDate(dateFromStr(timeCode));
    }

    /**
     * Parse the day of the given time code
     * @param timeCode {string}
     * @return {number} [1 .. 31]
     */
    parseDay(timeCode) {
        return Number(timeCode.split('-')[2]);
    }

    /**
     * Write a yearly time code
     * @param year {number} The year (integer)
     * @param bound {boolean} if true, the result will be inside this calculator's bounds (from, until)
     * @return {string}
     */
    writeYearly(year, bound = false) {
        year = !bound ? year : Math.max(Math.min(year, this.until ? this.getMaxYear() : year), this.from ? this.getMinYear() : year);
        return year + '-01-01';
    }

    /**
     * Write a monthly time code
     * @param year {number} The year (integer)
     * @param month {number} The month (integer)
     * @param bound {boolean} if true, the result will be inside this calculator's bounds (from, until)
     * @return {string}
     */
    writeMonthly(year, month, bound = false) {
        year = !bound ? year : Math.max(Math.min(year, this.until ? this.getMaxYear() : year), this.from ? this.getMinYear() : year);
        month = !bound ? month : Math.max(Math.min(month, this.until ? this.getMaxMonthForYear(year) : month), this.from ? this.getMinMonthForYear(year) : month);
        return year + '-' + month.pad(2) + '-01';
    }

    /**
     * Write a monthly forecast time code
     * @param year {number} The year (integer)
     * @param month {number} The month (integer)
     * @param bound {boolean} if true, the result will be inside this calculator's bounds (from, until)
     * @return {string}
     */
    writeMonthlyForecast(year, month, bound = false) {
        return this.writeMonthly(year, month, bound);
    }

    /**
     * Write a monthly updated ten daily time code
     * @param year {number} The year (integer)
     * @param month {number} The month (integer)
     * @param bound {boolean} if true, the result will be inside this calculator's bounds (from, until)
     * @return {string}
     */
    writeMonthlyUpdatedTenDaily(year, month, bound = false) {
        return this.writeMonthly(year, month, bound);
    }

    /**
     * Write yearless ten daily (with a 30-day scope) time code
     * @param month {number} The month (integer)
     * @param bound {boolean} if true, the result will be inside this calculator's bounds (from, until)
     * @return {string}
     */
    writeTenDailyYearless30Days(month, bound = false) {
        return this.writeMonthly(0, month, bound);
    }

    /**
     * Write a ten daily time code
     * @param year {number} The year (integer)
     * @param month {number} The month (integer)
     * @param tenDays {number} The ten day period (integer)
     * @param bound {boolean} if true, the result will be inside this calculator's bounds (from, until)
     * @return {string}
     */
    writeTenDaily(year = 0, month, tenDays, bound = false) {
        year = !bound ? year : Math.max(Math.min(year, this.until ? this.getMaxYear() : year), this.from ? this.getMinYear() : year);
        month = !bound ? month : Math.max(Math.min(month, this.until ? this.getMaxMonthForYear(year) : month), this.from ? this.getMinMonthForYear(year) : month);
        tenDays = !bound ? tenDays : Math.max(Math.min(tenDays, this.until ? this.getMaxTenDaysForYearMonth(year, month) : tenDays), this.from ? this.getMinTenDaysForYearMonth(year, month) : tenDays);
        let day;
        switch (tenDays) {
            case 1:
                day = 1;
                break;
            case 2:
                day = 11;
                break;
            case 3:
                day = 21;
                break;
            default:
                day = -1;
                break;
        }
        return year.pad(4) + '-' + month.pad(2) + '-' + day.pad(2);
    }

    /**
     * Write a weekly time code
     * @param year {number} The month (integer)
     * @param week {number} The week (integer)
     * @param bound {boolean} if true, the result will be inside this calculator's bounds (from, until)
     * @return {string}
     */
    writeWeekly(year, week, bound = false) {
        year = !bound ? year : Math.max(Math.min(year, this.until ? this.getMaxYear() : year), this.from ? this.getMinYear() : year);
        const firstDayOfFirstWeek = this.getFirstDayOfFirstWeek(year);
        const date = this.addDays(firstDayOfFirstWeek, (week - 1) * 7); //minus 1, because we aim at the beginning of the week. If we want week 1, we should add zero days
        return this.getYearFromDate(date) + '-' + this.getMonthFromDate(date).pad(2) + '-' + this.getDayFromDate(date).pad(2);
    }

    /**
     * Write an ISO weekly time code
     * @param year {number} The month (integer)
     * @param week {number} The week (integer)
     * @param bound {boolean} if true, the result will be inside this calculator's bounds (from, until)
     * @return {string}
     */
    writeISOWeekly(year, week, bound = false) {
        const firstDayOfFirstWeek = this.getFirstDayOfFirstISOWeek(year);
        const date = this.addDays(firstDayOfFirstWeek, (week - 1) * 7); //minus 1, because we aim at the beginning of the week. If we want week 1, we should add zero days
        return this.getYearFromDate(date) + '-' + this.getMonthFromDate(date).pad(2) + '-' + this.getDayFromDate(date).pad(2);
    }

    /**
     * Write a daily time code
     * @param year {number} The year (integer)
     * @param month {number} The month (integer)
     * @param day {number} The day (integer)
     * @param bound {boolean} if true, the result will be inside this calculator's bounds (from, until)
     * @return {string}
     */
    writeDaily(year, month, day, bound = false) {
        year = !bound ? year : Math.max(Math.min(year, this.until ? this.getMaxYear() : year), this.from ? this.getMinYear() : year);
        month = !bound ? month : Math.max(Math.min(month, this.until ? this.getMaxMonthForYear(year) : month), this.from ? this.getMinMonthForYear(year) : month);
        return year + '-' + month.pad(2) + '-' + Math.min(day, this.getMaxDayForYearMonth(year, month)).pad(2);
    }

    writeUnknown() {
        return '--';
    }

    /**
     * Given a date, write a time code
     * @param date {Date} The date
     * @param bound {boolean} if true, the result will be inside this calculator's bounds (from, until)
     * @return {string}
     */
    writeTimeCodeFromDate(date, bound = false) {
        if (!date)
            return undefined;
        date = dateFromStr(date);
        if (this.isYearly()) {
            return this.writeYearly(this.getYearFromDate(date), bound);
        }
        if (this.isWeekly()) {
            return this.writeWeekly(this.getYearFromDate(date), this.getWeekFromDate(date), bound);
        }
        if (this.isISOWeekly()) {
            return this.writeISOWeekly(this.getYearFromDate(date), this.getISOWeekFromDate(date), bound);
        }
        if (this.isMonthly()) {
            return this.writeMonthly(this.getYearFromDate(date), this.getMonthFromDate(date), bound);
        }
        if (this.isMonthlyForecast()) {
            return this.writeMonthlyForecast(this.getYearFromDate(date), this.getMonthFromDate(date), bound);
        }
        if (this.isTenDaily() || this.isMonthlyUpdatedTenDaily() || this.isTenDailyYearless() || this.isTenDailyYearless30Day()) {
            return this.writeTenDaily(this.getYearFromDate(date), this.getMonthFromDate(date), this.getTenDaysFromDate(date), bound);
        }
        if (this.isDaily() || this.isDailyYearless() || this.isLatest()) {
            return this.writeDaily(this.getYearFromDate(date), this.getMonthFromDate(date), this.getDayFromDate(date), bound);
        }
        return this.writeUnknown();
    }

    /**
     * Given a month a year, how many days are in the month?
     * @param month {number} The month (integer [1 .. 12])
     * @param year {number} The year (integer)
     * @return {number} (integer [1 .. 31])
     */
    daysInMonth(month, year) {
        return dateFromYearMonthDay(year, month + 1, 0).getDate();
    }

    /**
     * The last day of the month
     * @param year {number} (integer)
     * @param month {number} (integer)
     * @return {Date}
     */
    getLastDayOfMonth(year, month) {
        return dateFromYearMonthDay(year, month, this.daysInMonth(month, year))
    }

    /**
     * The last day of the month
     * @param date {Date}
     * @return {Date}
     */
    getLastDayOfMonthFromDate(date) {
        return this.getLastDayOfMonth(this.getYearFromDate(date), this.getMonthFromDate(date));
    }

    /**
     * Given a date, get the year
     * @param date {Date} The date
     * @return {number} (integer)
     */
    getYearFromDate(date) {
        return date.getFullYear();
    }

    /**
     * What is the min possible year?
     * @return {number|undefined}
     */
    getMinYear() {
        return this.from ? this.getYearFromDate(this.from) : undefined;
    }

    /**
     * What is the max possible year?
     * @return {number|undefined}
     */
    getMaxYear() {
        return this.until ? this.getYearFromDate(this.until) : undefined;
    }

    /**
     * Given a date, get the month
     * @param date {Date} The date
     * @return {number} (integer [1 .. 12])
     */
    getMonthFromDate(date) {
        return date.getMonth() + 1;
    }

    /**
     * What is the minimum month for the given year
     * @param year {number} the year (integer)
     * @return {number} (integer [1 .. 12])
     */
    getMinMonthForYear(year) {
        if (!this.from)
            return 1;
        if (this.isYearless())
            year = YEARLESS_YEAR;
        const fromYear = this.getYearFromDate(this.from);
        if (year === fromYear) {
            return this.getMonthFromDate(this.from);
        }
        return 1;
    }

    /**
     * What is the maximum month for the given year
     * @param year {number} the year (integer)
     * @return {number} (integer [1 .. 12])
     */
    getMaxMonthForYear(year) {
        if (!this.until)
            return 12;
        if (this.isYearless())
            year = YEARLESS_YEAR;
        const untilYear = this.getYearFromDate(this.until);
        if (year === untilYear) {
            return this.getMonthFromDate(this.until);
        }
        return 12;
    }

    /**
     * Given a date, get the day
     * @param date {Date} The date
     * @return {number} (integer [1 .. 31])
     */
    getDayFromDate(date) {
        return date.getDate();
    }

    /**
     * What is the minimum day for the given year and month
     * @param year {number} the year (integer)
     * @param month {number} the month (integer)
     * @return {number} (integer [1 .. 31])
     */
    getMinDayForYearMonth(year, month) {
        if (!this.from)
            return 1;
        if (this.isYearless()) {
            year = YEARLESS_YEAR;
        }
        const fromYear = this.getYearFromDate(this.from);
        const fromMonth = this.getMonthFromDate(this.from);
        if (year === fromYear && month === fromMonth) {
            return this.getDayFromDate(this.from);
        }
        return 1;
    }

    /**
     * What is the maximum day for the given year and month
     * @param year {number} the year (integer)
     * @param month {number} the month (integer)
     * @return {number} (integer [1 .. 31])
     */
    getMaxDayForYearMonth(year, month) {
        if (this.isYearless()) {
            year = YEARLESS_YEAR;
        }
        if (!this.until)
            return this.daysInMonth(month, year);
        const untilYear = this.getYearFromDate(this.until);
        const untilMonth = this.getMonthFromDate(this.until);
        if (year === untilYear && month === untilMonth) {
            return this.getDayFromDate(this.until);
        }
        return this.daysInMonth(month, year);
    }

    /**
     * What is the maximum day for the given year and month, without observing the max date of this time calculator
     * @param year {number} the year (integer)
     * @param month {number} the month (integer)
     * @return {number} (integer [1 .. 31])
     */
    getUnboundedMaxDayForYearMonth(year, month) {
        return this.daysInMonth(month, year);
    }

    /**
     * Given a date, get the ten day period
     * @param date {Date} The date
     * @return {number} (integer [1 .. 3])
     */
    getTenDaysFromDate(date) {
        const day = this.getDayFromDate(date);
        if (day < 11)
            return 1;
        if (day < 21)
            return 2;
        return 3;
    }

    /**
     * What is the minimum ten day period for the given year, month and min date
     * @param year {number} the year (integer)
     * @param month {number} the month (integer)
     * @return {number} (integer [1 .. 3])
     */
    getMinTenDaysForYearMonth(year, month) {
        if (!this.from)
            return 1;
        const fromYear = this.getYearFromDate(this.from);
        const fromMonth = this.getMonthFromDate(this.from);
        if (year === fromYear && month === fromMonth) {
            return this.getTenDaysFromDate(this.from);
        }
        return 1;
    }

    /**
     * What is the maximum ten day period for the given year and month
     * @param year {number} the year (integer)
     * @param month {number} the month (integer)
     * @return {number} (integer [1 .. 3])
     */
    getMaxTenDaysForYearMonth(year, month) {
        if (!this.until)
            return 3;
        const untilYear = this.getYearFromDate(this.until);
        const untilMonth = this.getMonthFromDate(this.until);
        if (year === untilYear && month === untilMonth) {
            return this.getTenDaysFromDate(this.until);
        }
        return 3;
    }

    /**
     * Given a date, get the week
     * @param date {Date} The date
     * @return {number} (integer [1 .. 53])
     */
    getWeekFromDate(date) {
        const firstDayOfFirstWeek = this.getFirstDayOfFirstWeek(this.getYearFromDate(date));
        const differenceInDays = this.getDifferenceInDays(date, firstDayOfFirstWeek)
            + (date.getDay() + 1); //plus day of week
        return Math.ceil(differenceInDays / 7);
    }

    /**
     * Given a date, get the ISO week
     * @param date {Date} The date
     * @return {number} (integer [1 .. 53])
     */
    getISOWeekFromDate(date) {
        const firstDayOfFirstWeek = this.getFirstDayOfFirstISOWeek(this.getYearFromDate(date));
        const differenceInDays = this.getDifferenceInDays(date, firstDayOfFirstWeek)
            + (date.getDay() + 1); //plus day of week
        return Math.ceil(differenceInDays / 7);
    }

    /**
     * What is the minimum week for the given year
     * @param year {number} the year (integer)
     * @return {number} (integer [1 .. 53])
     */
    getMinWeekForYear(year) {
        if (!this.from)
            return 1;
        const fromYear = this.getYearFromDate(this.from);
        if (year === fromYear) {
            return this.getWeekFromDate(this.from);
        }
        return 1;
    }

    /**
     * What is the minimum ISO week for the given year
     * @param year {number} the year (integer)
     * @return {number} (integer [1 .. 53])
     */
    getMinISOWeekForYear(year) {
        if (!this.from)
            return 1;
        const fromYear = this.getYearFromDate(this.from);
        if (year === fromYear) {
            return this.getISOWeekFromDate(this.from);
        }
        return 1;
    }

    /**
     * What is the maximum week for the given year
     * @param year {number} the year (integer)
     * @return {number} (integer [1 .. 53])
     */
    getMaxWeekForYear(year) {
        const firstDayOfFirstWeek = this.getFirstDayOfFirstWeek(year);
        const lastDayOfLastWeek = this.min(this.until, this.addDays(this.getFirstDayOfLastWeek(year), 6));
        return Math.ceil(this.getDifferenceInDays(firstDayOfFirstWeek, lastDayOfLastWeek) / 7);
    }

    /**
     * What is the maximum ISO week for the given year
     * @param year {number} the year (integer)
     * @return {number} (integer [1 .. 53])
     */
    getMaxISOWeekForYear(year) {
        const firstDayOfFirstWeek = this.getFirstDayOfFirstISOWeek(year);
        const lastDayOfLastWeek = this.min(this.until, this.addDays(this.getFirstDayOfLastISOWeek(year), 6));
        return Math.ceil(this.getDifferenceInDays(firstDayOfFirstWeek, lastDayOfLastWeek) / 7);
    }

    /**
     * Add days to the given date
     * @param date {Date} The given date
     * @param days {number} the nr of days (integer)
     * @return {Date}
     */
    addDays(date, days) {
        return dateFromYearMonthDay(this.getYearFromDate(date), this.getMonthFromDate(date), this.getDayFromDate(date) + days);
    }

    /**
     * Add months to the given date
     * @param date {Date} The given date
     * @param months {number} the nr of months (integer)
     * @return {Date}
     */
    addMonths(date, months) {
        return new Date(date.setMonth(date.getMonth() + months));
    }

    /**
     * Add years to the given date
     * @param date {Date} The given date
     * @param years {number} the nr of years (integer)
     * @return {Date}
     */
    addYears(date, years) {
        return dateFromYearMonthDay(this.getYearFromDate(date) + years, this.getMonthFromDate(date), this.getDayFromDate(date));
    }

    /**
     * Maximum of 2 dates
     * @param date1 {Date}
     * @param date2 {Date}
     * @return {Date}
     */
    max(date1, date2) {
        if (!date1)
            return date2;
        if (!date2)
            return date1;
        return date1.getTime() > date2.getTime() ? date1 : date2;
    }

    /**
     * Minimum of 2 dates
     * @param date1 {Date}
     * @param date2 {Date}
     * @return {Date}
     */
    min(date1, date2) {
        if (!date1)
            return date2;
        if (!date2)
            return date1;
        return date1.getTime() < date2.getTime() ? date1 : date2;
    }

    /**
     * Nr of days between 2 dates
     * @param date1 {Date}
     * @param date2 {Date}
     * @return {number} (integer)
     */
    getDifferenceInDays(date1, date2) {
        const diffTime = Math.abs(date2 - date1);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }

    /**
     * The first day of the first week of the given year
     * @param year {number} (integer)
     * @return {Date}
     */
    getFirstDayOfFirstWeek(year) {
        //Javascript weekdays are zero-based and start on a sunday
        //Our weeks start on mondays. We want the one that contains the first sunday of the year
        const firstDayOfYear = dateFromYearMonthDay(year, 1, 1);
        let firstDayOfFirstWeek;
        if (firstDayOfYear.getDay() === 1) { //first day is a monday. we found it
            firstDayOfFirstWeek = firstDayOfYear
        } else { //let's go back some days to find the previous monday
            firstDayOfFirstWeek = this.addDays(firstDayOfYear, -this.addDays(firstDayOfYear, -1).getDay());
        }
        return firstDayOfFirstWeek;
    }

    /**
     * The first day of the first ISO week of the given year
     * @param year {number} (integer)
     * @return {Date}
     */
    getFirstDayOfFirstISOWeek(year) {
        //let's find the monday right before the 4th of january
        const _4thOfJanuary = dateFromYearMonthDay(year, 1, 4);
        return this.addDays(_4thOfJanuary, 1 - _4thOfJanuary.getDay());
    }

    /**
     * The first day of the last week of the given year
     * @param year {number} (integer)
     * @return {Date}
     */
    getFirstDayOfLastWeek(year) {
        const firstDayOfFirstWeek = this.getFirstDayOfFirstWeek(year + 1);
        return this.addDays(firstDayOfFirstWeek, -7);
    }

    /**
     * The first day of the last ISO week of the given year
     * @param year {number} (integer)
     * @return {Date}
     */
    getFirstDayOfLastISOWeek(year) {
        const firstDayOfFirstWeek = this.getFirstDayOfFirstISOWeek(year + 1);
        return this.addDays(firstDayOfFirstWeek, -7);
    }

    /**
     * Get a human-readable string given a time code
     * @param timeCode {string} the time code
     * @return {string|undefined}
     */
    timeCodeToString(timeCode) {
        if (this.isUnknown()) {
            return undefined;
        }
        if (this.isDaily() || this.isLatest()) {
            return formatDate(timeCode);
        }
        if (this.isDailyYearless()) {
            return formatDateDailyYearless(timeCode);
        }
        const startDate = dateFromStr(timeCode);
        let endDate = this.getEndDateOfPeriod(startDate);

        if (this.isMonthlyForecast()) {
            return this.translator.translate('layer.timeStrMonthlyForecast', [formatDate(startDate)]);
        }
        if (this.isMonthlyUpdatedTenDaily()) {
            return this.translator.translate('layer.timeStrMonthlyUpdatedTenDaily', [formatDate(this.getStartDateOfPeriod(startDate)), formatDate(this.getEndDateOfPeriod(startDate))]);
        }
        if (this.isWeekly()) {
            return this.translator.translate('layer.timeStrWeekly', [this.getWeekFromDate(startDate), formatDate(startDate), formatDate(endDate)]);
        }
        if (this.isISOWeekly()) {
            return this.translator.translate('layer.timeStrISOWeekly', [this.getISOWeekFromDate(startDate), formatDate(startDate), formatDate(endDate)]);
        }
        if (this.isTenDailyYearless() || this.isTenDailyYearless30Day()) {
            return this.translator.translate('layer.timeStr', [formatDateTenDailyYearless(startDate), formatDateTenDailyYearless(endDate)])
        }
        return this.translator.translate('layer.timeStr', [formatDate(startDate), formatDate(endDate)]);
    }

    getStartDateOfPeriod(startDate) {
        if (!startDate || this.isUnknown()) {
            return undefined;
        }
        startDate = dateFromStr(startDate);
        if (this.isDaily() || this.isDailyYearless() || this.isLatest()) {
            return startDate;
        }
        if (this.isYearly()) {
            return dateFromStr(this.getYearFromDate(startDate) + '-01-01');
        }
        if (this.isMonthly() || this.isMonthlyForecast()) {
            return dateFromYearMonthDay(this.getYearFromDate(startDate), this.getMonthFromDate(startDate), 1);
        }

        if (this.isTenDaily() || this.isMonthlyUpdatedTenDaily() || this.isTenDailyYearless() || this.isTenDailyYearless30Day()) {
            const getStartDateForTenDaily = (d) => {
                const day = this.getDayFromDate(d);
                if (day < 11)
                    return dateFromYearMonthDay(this.getYearFromDate(d), this.getMonthFromDate(d), 1);
                else if (day < 21)
                    return dateFromYearMonthDay(this.getYearFromDate(d), this.getMonthFromDate(d), 11);
                else return dateFromYearMonthDay(this.getYearFromDate(d), this.getMonthFromDate(d), 21);
            }
            if (this.isMonthlyUpdatedTenDaily() || this.isTenDailyYearless30Day()) {
                const publishDate = getStartDateForTenDaily(startDate);
                return this.getPreviousDate(this.getPreviousDate(publishDate));
            }
            return getStartDateForTenDaily(startDate);
        }
        if (this.isWeekly()) {
            const weekFromDate = this.getWeekFromDate(startDate);
            return dateFromStr(this.writeWeekly(this.getYearFromDate(startDate), weekFromDate))
        }
        if (this.isISOWeekly()) {
            const weekFromDate = this.getISOWeekFromDate(startDate);
            return dateFromStr(this.writeISOWeekly(this.getYearFromDate(startDate), weekFromDate))
        }
    }

    getEndDateOfPeriod(startDate) {
        if (!startDate || this.isUnknown()) {
            return undefined;
        }
        startDate = dateFromStr(startDate);

        if (this.isMonthlyUpdatedTenDaily() || this.isTenDailyYearless30Day()) {
            return this.addDays(this.getNextDate(startDate), -1);
        }

        startDate = this.getStartDateOfPeriod(startDate);
        if (this.isDaily() || this.isDailyYearless() || this.isLatest()) {
            return startDate;
        }
        if (this.isYearly()) {
            return dateFromStr(this.getYearFromDate(startDate) + '-12-31');
        }
        if (this.isMonthly() || this.isMonthlyForecast()) {
            return dateFromYearMonthDay(this.getYearFromDate(startDate), this.getMonthFromDate(startDate), this.daysInMonth(this.getMonthFromDate(startDate), this.getYearFromDate(startDate)));
        }
        if (this.isTenDaily() || this.isTenDailyYearless()) {
            const tenDays = this.parseTenDays(formatDate(startDate));
            switch (tenDays) {
                case 3:
                    return dateFromYearMonthDay(this.getYearFromDate(startDate), this.getMonthFromDate(startDate), this.daysInMonth(this.getMonthFromDate(startDate), this.getYearFromDate(startDate)));
                case 2:
                    return dateFromYearMonthDay(this.getYearFromDate(startDate), this.getMonthFromDate(startDate), 20);
                case 1:
                    return dateFromYearMonthDay(this.getYearFromDate(startDate), this.getMonthFromDate(startDate), 10);
            }
        }
        if (this.isWeekly() || this.isISOWeekly()) {
            return this.addDays(startDate, 6);
        }
        return undefined;
    }

    getMiddleDateOfPeriod(startDate) {
        if (!startDate || this.isUnknown()) {
            return undefined;
        }
        startDate = dateFromStr(startDate);

        if (this.isMonthlyUpdatedTenDaily() || this.isTenDailyYearless30Day()) {
            return this.addDays(this.getEndDateOfPeriod(startDate), -15);
        }

        startDate = this.getStartDateOfPeriod(startDate);
        if (this.isDaily() || this.isDailyYearless() || this.isLatest()) {
            return startDate;
        }
        if (this.isYearly()) {
            return dateFromStr(this.getYearFromDate(startDate) + '-06-30');
        }
        if (this.isMonthly() || this.isMonthlyForecast()) {
            return dateFromYearMonthDay(this.getYearFromDate(startDate), this.getMonthFromDate(startDate), 15);
        }
        if (this.isTenDaily() || this.isTenDailyYearless()) {
            return this.addDays(this.getEndDateOfPeriod(startDate), -5);
        }
        if (this.isWeekly() || this.isISOWeekly()) {
            return this.addDays(this.getEndDateOfPeriod(startDate), -3);
        }
        return undefined;
    }

    /**
     * Fix a time code
     * @param timeCode {string} The time code (YYYY-MM-DD)
     * @return {string} (YYYY-MM-DD)
     */
    fixTimeCode(timeCode) {
        if (!nnTrim(timeCode))
            return undefined;
        return formatDate(this.fixDate(dateFromStr(timeCode)));
    }

    /**
     * Fix a time code
     * @param date {Date}
     * @return {Date}
     */
    fixDate(date) {
        if (!date)
            return date;

        let fixedDate = date;
        fixedDate.setHours(0)
        fixedDate.setMinutes(0)
        fixedDate.setSeconds(0)

        if (this.isLatest())
            return fixedDate;

        if (this.isYearless()) {
            let res = this.isDailyYearless()
                ? dateFromStr(this.writeDaily(YEARLESS_YEAR, this.getMonthFromDate(fixedDate), this.getDayFromDate(fixedDate)))
                : this.isTenDailyYearless() || this.isTenDailyYearless30Day()
                    ? dateFromStr(this.writeTenDaily(YEARLESS_YEAR, this.getMonthFromDate(fixedDate), this.getTenDaysFromDate(fixedDate)))
                    : fixedDate;
            if (this.from)
                res = this.max(this.from, res);
            if (this.until)
                res = this.min(this.until, res);
            return res;
        }

        if (this.from)
            fixedDate = this.max(this.from, fixedDate);
        if (this.until)
            fixedDate = this.min(this.until, fixedDate);

        if (this.isDaily()) {
            return dateFromStr(this.writeDaily(this.getYearFromDate(fixedDate), this.getMonthFromDate(fixedDate), this.getDayFromDate(fixedDate)));
        }
        if (this.isWeekly()) {
            return dateFromStr(this.writeWeekly(this.getYearFromDate(fixedDate), this.getWeekFromDate(fixedDate)));
        }
        if (this.isISOWeekly()) {
            return dateFromStr(this.writeISOWeekly(this.getYearFromDate(fixedDate), this.getISOWeekFromDate(fixedDate)));
        }
        if (this.isTenDaily() || this.isMonthlyUpdatedTenDaily()) {
            return dateFromStr(this.writeTenDaily(this.getYearFromDate(fixedDate), this.getMonthFromDate(fixedDate), this.getTenDaysFromDate(fixedDate)));
        }
        if (this.isMonthly() || this.isMonthlyForecast()) {
            return dateFromStr(this.writeMonthly(this.getYearFromDate(fixedDate), this.getMonthFromDate(fixedDate)));
        }
        if (this.isYearly()) {
            return dateFromStr(this.writeYearly(this.getYearFromDate(fixedDate)));
        }
        return undefined;
    }

    /**
     * Fix the end of a time frame
     * @param until {Date} The end of the time frame
     * @return {Date}
     */
    fixDateUntil(until) {
        const from = this.fixDate(until);
        if (!from || this.isLatest() || this.isDaily() || this.isDailyYearless()) {
            return from;
        }
        const yearFromDate = this.getYearFromDate(from);
        const monthFromDate = this.getMonthFromDate(from);
        if (this.isWeekly() || this.isISOWeekly()) {
            return this.min(
                dateFromYearMonthDay(yearFromDate, monthFromDate, this.getMaxDayForYearMonth(yearFromDate, monthFromDate)),
                this.addDays(from, 6)
            );
        }
        if (this.isTenDaily() || this.isMonthlyUpdatedTenDaily() || this.isTenDailyYearless() || this.isTenDailyYearless30Day()) {
            const tenDays = this.parseTenDays(formatDate(from));
            switch (tenDays) {
                case 3:
                    return dateFromYearMonthDay(yearFromDate, monthFromDate, this.daysInMonth(monthFromDate, yearFromDate));
                default:
                    return this.addDays(from, 9);
            }
        }
        if (this.isMonthly() || this.isMonthlyForecast()) {
            return dateFromYearMonthDay(yearFromDate, monthFromDate, this.getMaxDayForYearMonth(yearFromDate, monthFromDate));
        }
        if (this.isYearly()) {
            return dateFromYearMonthDay(yearFromDate, 12, this.getMaxDayForYearMonth(yearFromDate, 12));
        }
        return undefined;
    }

    /**
     * Fix the end of a time frame, without observing the max date of this time calculator
     * @param until {Date} The end of the time frame
     * @return {Date}
     */
    fixUnboundedDateUntil(until) {
        const from = this.fixDate(until);
        if (!from || this.isLatest() || this.isDaily() || this.isDailyYearless()) {
            return from;
        }
        if (this.isWeekly() || this.isISOWeekly()) {
            return this.addDays(from, 6);
        }

        const yearFromDate = this.getYearFromDate(from);
        if (this.isYearly()) {
            return dateFromYearMonthDay(yearFromDate, 12, 31);
        }

        const monthFromDate = this.getMonthFromDate(from);
        if (this.isTenDaily() || this.isMonthlyUpdatedTenDaily() || this.isTenDailyYearless() || this.isTenDailyYearless30Day()) {
            return this.min(
                dateFromYearMonthDay(yearFromDate, monthFromDate, this.getUnboundedMaxDayForYearMonth(yearFromDate, monthFromDate)),
                this.addDays(from, 9)
            );
        }
        if (this.isMonthly() || this.isMonthlyForecast()) {
            return dateFromYearMonthDay(yearFromDate, monthFromDate, this.getUnboundedMaxDayForYearMonth(yearFromDate, monthFromDate));
        }
        return undefined;
    }

    /**
     * Get an array of dates for a time series
     * @param from {Date}
     * @param until {Date}
     * @param unbounded {boolean} should the result be not bounded by this calculators date bounds?
     * @return {Array<Date>}
     */
    getTimeSeries(from, until, unbounded = false) {
        const dates = [from];
        const fixedUntilMillis = this.fixDate(until).getTime();
        while (arrayLast(dates).getTime() < fixedUntilMillis) // we fix the date to get to the beginning of the last period
            dates.push(this.getNextDate(arrayLast(dates), !unbounded))
        return dates;
    }

    /**
     * Get sample data (ramdom), given time bounds
     * @param from {Date}
     * @param until {Date}
     * @param range {Array<number>|Array<string>} Array of 2 numbers or array of n strings
     * @return {Array<number>|Array<string>}
     */
    getSampleData(from, until, range) {
        const data = [];
        if (from && until) {
            this.getTimeSeries(this.fixDate(from), this.fixDateUntil(until)).forEach(date => {
                if (!range)
                    data.push((Math.random() * 10 + 1) * (Math.random() > 0.5 ? 1 : -1));
                else if (isNumber(range[0]))
                    data.push(getRandomNumber(range[0], range[1]));
                else if (isString(range[0]))
                    data.push(getRandomStr(range));
            });
        }
        return data;
    }

    /**
     * Given a date, get the previous date (beginning of period)
     * @param date {Date}
     * @param bound {boolean} if true, the result will be inside this calculator's bounds (from, until)
     * @return {Date}
     */
    getPreviousDate(date, bound = false) {
        if (this.isDaily() || this.isDailyYearless()) {
            date = this.addDays(date, -1);
            return dateFromStr(this.writeDaily(this.getYearFromDate(date), this.getMonthFromDate(date), this.getDayFromDate(date), bound));
        }

        if (this.isWeekly()) {
            date = this.addDays(this.getStartDateOfPeriod(date), -7);
            return dateFromStr(this.writeWeekly(this.getYearFromDate(date), this.getWeekFromDate(date), bound));
        }
        if (this.isISOWeekly()) {
            date = this.addDays(this.getStartDateOfPeriod(date), -7);
            return dateFromStr(this.writeISOWeekly(this.getYearFromDate(date), this.getISOWeekFromDate(date), bound));
        }

        date = this.fixDate(date);
        const yearFromDate = this.getYearFromDate(date);
        if (this.isYearly()) {
            return dateFromStr(this.writeYearly(yearFromDate - 1));
        }

        const monthFromDate = this.getMonthFromDate(date);
        if (this.isTenDaily() || this.isMonthlyUpdatedTenDaily() || this.isTenDailyYearless() || this.isTenDailyYearless30Day()) {
            const tenDays = this.parseTenDays(formatDate(date));
            if (tenDays > 1)
                return dateFromStr(this.writeTenDaily(yearFromDate, monthFromDate, tenDays - 1, bound));
            if (monthFromDate > 1)
                return dateFromStr(this.writeTenDaily(yearFromDate, monthFromDate - 1, 3, bound));
            else return dateFromStr(this.writeTenDaily(yearFromDate - 1, 12, 3, bound));
        }
        if (this.isMonthly() || this.isMonthlyForecast()) {
            if (monthFromDate > 1)
                return dateFromStr(this.writeMonthly(yearFromDate, monthFromDate - 1, bound));
            else return dateFromStr(this.writeMonthly(yearFromDate - 1, 12, bound));
        }
    }

    /**
     * Given a date, get the next date (beginning of period)
     * @param date {Date}
     * @param bound {boolean} if true, the result will be inside this calculator's bounds (from, until)
     * @return {Date}
     */
    getNextDate(date, bound = false) {
        if (this.isDaily() || this.isDailyYearless()) {
            date = this.addDays(date, 1);
            return dateFromStr(this.writeDaily(this.getYearFromDate(date), this.getMonthFromDate(date), this.getDayFromDate(date), bound));
        }

        if (this.isWeekly()) {
            date = this.addDays(this.getStartDateOfPeriod(date), 7);
            return dateFromStr(this.writeWeekly(this.getYearFromDate(date), this.getWeekFromDate(date), bound));
        }
        if (this.isISOWeekly()) {
            date = this.addDays(this.getStartDateOfPeriod(date), 7);
            return dateFromStr(this.writeISOWeekly(this.getYearFromDate(date), this.getISOWeekFromDate(date), bound));
        }

        date = this.fixDate(date);
        const yearFromDate = this.getYearFromDate(date);
        if (this.isYearly()) {
            return dateFromStr(this.writeYearly(yearFromDate + 1, bound));
        }

        const monthFromDate = this.getMonthFromDate(date);
        if (this.isTenDaily() || this.isMonthlyUpdatedTenDaily() || this.isTenDailyYearless() || this.isTenDailyYearless30Day()) {
            const tenDays = this.parseTenDays(formatDate(date));
            if (tenDays < 3)
                return dateFromStr(this.writeTenDaily(yearFromDate, monthFromDate, tenDays + 1, bound));
            if (monthFromDate < 12)
                return dateFromStr(this.writeTenDaily(yearFromDate, monthFromDate + 1, 1, bound));
            else return dateFromStr(this.writeTenDaily(yearFromDate + 1, 1, 1, bound));
        }
        if (this.isMonthly() || this.isMonthlyForecast()) {
            if (monthFromDate < 12)
                return dateFromStr(this.writeMonthly(yearFromDate, monthFromDate + 1, bound));
            else return dateFromStr(this.writeMonthly(yearFromDate + 1, 1, bound));
        }
    }

    getLatestDate(bound = false) {
        if (this.isYearless()) {
            const now = new Date();
            const day = this.getDayFromDate(now);
            const month = this.getMonthFromDate(now);
            return this.writeTimeCodeFromDate(this.fixDate(dateFromYearMonthDay(YEARLESS_YEAR, month, day)), bound);
        }
        return this.writeTimeCodeFromDate(this.getUntil(), bound);
    }
}

/**
 *
 * @param ts {Date}
 * @param includeSeconds {boolean}
 * @return {string}
 */
function formatTimestamp(ts, includeSeconds = false) {
    if (!ts)
        return undefined;
    if (isString(ts))
        ts = dateFromStr(ts);
    const datePart = formatDate(ts);
    if (!datePart)
        return datePart;
    return datePart + ' ' + ts.getHours().pad(2) + ':' + ts.getMinutes().pad(2) + (includeSeconds ? (':' + ts.getSeconds().pad(2)) : '');
}

/**
 *
 * @param date {Date}
 * @return {string}
 */
function formatDate(date) {
    if (!date)
        return undefined;
    if (isString(date))
        date = dateFromStr(date);
    if (!(date instanceof Date))
        return date;
    return (date.getFullYear() === 0
        ? [(date.getMonth() + 1).pad(2), date.getDate().pad(2)]
        : [date.getFullYear(), (date.getMonth() + 1).pad(2), date.getDate().pad(2)]).join('-')
}

/**
 *
 * @param date {Date}
 * @return {string}
 */
function formatDateMonthly(date) {
    if (!date)
        return undefined;
    if (isString(date))
        date = dateFromStr(date);
    if (!(date instanceof Date))
        return date;
    return [date.getFullYear(), (date.getMonth() + 1).pad(2)].join('-');
}

/**
 *
 * @param date {Date}
 * @return {string}
 */
function formatDateTenDailyYearless(date) {
    return formatDateDailyYearless(date);
}

/**
 *
 * @param date {Date}
 * @return {string}
 */
function formatDateDailyYearless(date) {
    if (!date)
        return undefined;
    if (isString(date))
        date = dateFromStr(date);
    if (!(date instanceof Date))
        return date;
    return [(date.getMonth() + 1).pad(2), date.getDate().pad(2)].join('-');
}

/**
 * Verify if a date is valid
 * @param str A string in ISO form: yyyy-mm-dd
 * @returns {boolean} true if valid
 */
function verifyDateString(str) {
    try {
        Date.parse(dateFromStr(str).toISOString());
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Parse a date from a string
 * @param str {string} The given string
 * @return {Date}
 */
function dateFromStr(str) {
    str = nnTrim(str);
    if (!str)
        return str;
    if (str instanceof Date)
        return str;
    if (!(isString(str)))
        return undefined;
    if (str.indexOf('T') > 0 || str.indexOf(' ') > 0)
        return new Date(str);
    const split = str.split('-');
    let year, month, day;
    if (split.length === 1) {
        year = Number(split[0]);
        month = 1;
        day = 1;
    } else if (split.length === 2) {
        const firstComponent = Number(split[0]);
        const isYearless = firstComponent > 0 && firstComponent <= 12;
        year = isYearless ? YEARLESS_YEAR : firstComponent;
        month = isYearless ? firstComponent : Number(split[1]);
        day = isYearless ? Number(split[1]) : 1;
    } else if (split.length === 3) {
        year = Number(split[0]);
        month = Number(split[1]);
        day = Number(split[2]);
    }
    return new Date([year, month.pad(2), day.pad(2)].join('-') + ' 00:00:00.00');
}

/**
 *
 * @param year {number} (integer)
 * @param month {number} (integer [1 .. 12])
 * @param day  {number} (integer [1 .. 31])
 * @return {Date}
 */
function dateFromYearMonthDay(year = 0, month = 1, day = 1) {
    const date = new Date(year, month - 1, day);
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return new Date(Date.parse(date.toUTCString()));
}

export {
    TimeCalculator,
    verifyDateString,
    dateFromStr,
    formatDate,
    formatTimestamp,
    formatDateMonthly,
    formatDateDailyYearless,
    formatDateTenDailyYearless,
    dateFromYearMonthDay
};
